import { motion } from 'framer-motion'
import { Check, X, ArrowRight, Sparkles, ChevronDown, ChevronUp } from 'lucide-react'
import { ReactNode, useState } from 'react'

interface PricingFeature {
  text: string
  included: boolean
}

interface PricingCardProps {
  name: string
  icon: ReactNode
  price: string | number
  period?: string
  description: string
  features: PricingFeature[]
  hiddenFeatures?: PricingFeature[]
  buttonText: string
  buttonLink?: string
  isPopular?: boolean
  gradient: string
  delay?: number
  showMoreText?: string
  showLessText?: string
}

export default function PricingCard({
  name,
  icon,
  price,
  period = '/mo',
  description,
  features,
  hiddenFeatures = [],
  buttonText,
  buttonLink = '#',
  isPopular = false,
  gradient,
  delay = 0,
  showMoreText = 'Show more',
  showLessText = 'Show less',
}: PricingCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const hasHiddenFeatures = hiddenFeatures.length > 0
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      className={`relative bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-lg border-2 transition-all duration-300 hover:shadow-2xl h-full flex flex-col ${
        isPopular
          ? 'border-purple-500 dark:border-purple-400 ring-4 ring-purple-100 dark:ring-purple-900/50'
          : 'border-gray-200 dark:border-slate-700'
      } ${isPopular ? 'transform hover:scale-105' : 'hover:scale-102'}`}
    >
      {/* Popular Badge */}
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1 shadow-lg">
            <Sparkles className="w-4 h-4" />
            Popular
          </div>
        </div>
      )}

      {/* Icon */}
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white mb-3`}>
        {icon}
      </div>

      {/* Plan Name */}
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{name}</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{description}</p>

      {/* Price */}
      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold text-gray-900 dark:text-white">
            {typeof price === 'number' ? `$${price}` : price}
          </span>
          {period && <span className="text-gray-600 dark:text-gray-400 text-base">{period}</span>}
        </div>
      </div>

      {/* Features */}
      <ul className="space-y-2.5 mb-4 flex-grow">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            {feature.included ? (
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            ) : (
              <X className="w-5 h-5 text-gray-300 dark:text-gray-600 flex-shrink-0 mt-0.5" />
            )}
            <span
              className={`text-sm ${
                feature.included
                  ? 'text-gray-700 dark:text-gray-300'
                  : 'text-gray-400 dark:text-gray-600 line-through'
              }`}
            >
              {feature.text}
            </span>
          </li>
        ))}
        {isExpanded && hiddenFeatures.map((feature, index) => (
          <li key={`hidden-${index}`} className="flex items-start gap-3">
            {feature.included ? (
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            ) : (
              <X className="w-5 h-5 text-gray-300 dark:text-gray-600 flex-shrink-0 mt-0.5" />
            )}
            <span
              className={`text-sm ${
                feature.included
                  ? 'text-gray-700 dark:text-gray-300'
                  : 'text-gray-400 dark:text-gray-600 line-through'
              }`}
            >
              {feature.text}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        {/* Show More/Less Button */}
        {hasHiddenFeatures && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4 font-medium transition-colors"
          >
            {isExpanded ? (
              <>
                {showLessText}
                <ChevronUp className="w-3.5 h-3.5" />
              </>
            ) : (
              <>
                {showMoreText}
                <ChevronDown className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        )}

        {!hasHiddenFeatures && <div className="mb-1" />}

        {/* CTA Button */}
        <a
          href={buttonLink}
          target="_blank"
          rel="noopener noreferrer"
          className={`block w-full py-2.5 px-5 rounded-xl font-semibold text-sm text-center transition-all duration-300 flex items-center justify-center gap-2 ${
          isPopular
            ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-xl'
            : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white hover:shadow-lg'
        }`}
      >
        {buttonText}
        <ArrowRight className="w-4 h-4" />
      </a>
      </div>
    </motion.div>
  )
}
